import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchMe, logout as apiLogout } from './api';

const AuthContext = createContext({ user: null, loading: true, logout: () => {}, refresh: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await fetchMe();
      setUser(u);
    } catch (err) {
      // Only treat an explicit 401 as "your session is invalid". Network
      // errors (502/504/timeouts/CORS/dropped connections) are transient
      // — they typically mean the backend is busy or the edge proxy
      // dropped the request. Clearing `user` on those would log the
      // broker out mid-session every time a slow background job ran or
      // the LLM upstream hiccupped. Leave the existing user in place so
      // ProtectedRoute keeps rendering and the next poll can recover.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Skip auth poll on public widget routes (root + future public pages).
    // Admin routes still trigger the poll so ProtectedRoute can gate properly.
    const path = window.location.pathname || '/';
    if (path === '/' || (!path.startsWith('/admin') && !path.startsWith('/dashboard') && !path.startsWith('/rates') && !path.startsWith('/promos') && !path.startsWith('/policies') && !path.startsWith('/predictor') && !path.startsWith('/picker') && !path.startsWith('/buydown') && !path.startsWith('/history') && !path.startsWith('/documents') && !path.startsWith('/lenders') && !path.startsWith('/settings') && !path.startsWith('/search'))) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async () => {
    await apiLogout();
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
